import React, { useMemo, useEffect } from 'react'
import MiniLoading from 'Components/MiniLoading'
import FilteringTable from 'Components/FilteringTable'
import No from '../../Assets/Images/Login/no.svg'
import useInventoryProduct from 'Hooks/useInventoryProduct'
import { Modal } from 'react-bootstrap'
import { VAPermissions } from 'Utils/constants'
import useValidatePermission from 'Hooks/useValidatePermission'

/**
 * Componente para visualizar el inventario de un producto
 * @param {Object} props - Propiedades del componente
 * @returns {JSX.Element} - Componente de inventario
 */
const InventoryModal = ({ isOpen, onClose, productId: externalProductId }) => {
  // Usar el hook personalizado para manejar la lógica de inventario
  const {
    setProductId,
    inventoryData,
    loadingInventary,
    hiddenColumnsInventory,
    setHiddenColumnsInventory,
    getArrayHiddenColumns,
    handleSortChange,
  } = useInventoryProduct()

  const canViewCost = useValidatePermission(VAPermissions.seeCostProd)

  // Referencia para evitar cargas duplicadas
  const loadedProductsRef = React.useRef({})

  // Cuando el modal se abre o cambia el productId, cargar los datos
  useEffect(() => {
    // Solo cargar datos cuando el modal está abierto y tenemos un productId
    if (isOpen && externalProductId) {
      // Solo necesitamos establecer el productId, el useEffect dentro de
      // useInventory se encargará de llamar a fetchAllWarehousesAvailability
      if (!loadedProductsRef.current[externalProductId]) {
        loadedProductsRef.current[externalProductId] = true
        setProductId(externalProductId)
      }
    } else if (!isOpen) {
      // Resetear cuando se cierra el modal
      loadedProductsRef.current = {}
    }
  }, [isOpen, externalProductId])

  /**
   * Define las columnas para la tabla de inventario
   */
  const columnsInventory = useMemo(
    () => [
      {
        Header: 'Tienda',
        accessor: 'Tienda',
        hideEnabled: true,
        Footer: 'Total general',
        disableSortBy: true,
      },
      {
        Header: 'Código',
        hideEnabled: true,
        accessor: 'Código',
        disableSortBy: true,
      },
      ...(canViewCost
        ? [
            {
              Header: 'Costo',
              hideEnabled: true,
              accessor: 'Costo',
              disableSortBy: true,
            },
          ]
        : []),
      {
        Header: 'Disponible',
        hideEnabled: true,
        accessor: 'Disponible',
        sortDescFirst: true,
        Footer: info => {
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) =>
                  parseInt(
                    row.values.Disponible.toString().replaceAll(',', '')
                  ) + sum,
                0
              ),
            [info.rows]
          )
          return <>{total}</>
        },
      },
      {
        Header: 'En Existencia',
        hideEnabled: true,
        accessor: 'En Existencia',
        sortDescFirst: true,
        Footer: info => {
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) =>
                  parseInt(
                    row.values['En Existencia'].toString().replaceAll(',', '')
                  ) + sum,
                0
              ),
            [info.rows]
          )
          return <>{total}</>
        },
      },
      {
        Header: 'Comprometido',
        hideEnabled: true,
        accessor: 'Comprometido',
        sortDescFirst: true,
        Footer: info => {
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) =>
                  parseInt(
                    row.values.Comprometido.toString().replaceAll(',', '')
                  ) + sum,
                0
              ),
            [info.rows]
          )
          return <>{total}</>
        },
      },
      {
        Header: 'En Compras',
        hideEnabled: true,
        accessor: 'En Compras',
        sortDescFirst: true,
        Footer: info => {
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) =>
                  parseInt(
                    row.values['En Compras'].toString().replaceAll(',', '')
                  ) + sum,
                0
              ),
            [info.rows]
          )
          return <>{total}</>
        },
      },
      {
        Header: 'Requisición',
        hideEnabled: true,
        accessor: 'Requisición',
        sortDescFirst: true,
        Footer: info => {
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) =>
                  parseInt(
                    row.values['Requisición'].toString().replaceAll(',', '')
                  ) + sum,
                0
              ),
            [info.rows]
          )
          return <>{total}</>
        },
      },
      {
        Header: 'Traslado',
        hideEnabled: true,
        accessor: 'Traslado',
        sortDescFirst: true,
        Footer: info => {
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) =>
                  parseInt(row.values.Traslado.toString().replaceAll(',', '')) +
                  sum,
                0
              ),
            [info.rows]
          )
          return <>{total}</>
        },
      },
      {
        Header: 'Total',
        hideEnabled: true,
        accessor: 'Total',
        sortDescFirst: true,
        Footer: info => {
          const total = React.useMemo(
            () =>
              info.rows.reduce(
                (sum, row) =>
                  parseInt(row.values.Total.toString().replaceAll(',', '')) +
                  sum,
                0
              ),
            [info.rows]
          )
          return <>{total}</>
        },
      },
    ],
    []
  )

  // Manejar el cierre del modal
  const handleClose = () => {
    // No establecer productId a null para evitar cargas innecesarias
    // setProductId(null); - Eliminamos esta línea
    onClose()
  }

  return (
    <Modal
      show={isOpen}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      fullscreen={true}
      data-testid="container-inventory-modal">
      <div
        className="inventory-modal-content"
        data-testid="container-inventory-modal-content">
        <div
          className="inventory-header"
          data-testid="container-inventory-modal-header">
          <h2 data-testid="title-inventory-modal">Inventario</h2>
          <img
            style={{ cursor: 'pointer' }}
            src={No}
            alt="closing button"
            onClick={handleClose}
            data-testid="btn-close-inventory-modal"
          />
        </div>
        {loadingInventary ? (
          <MiniLoading noMargin={true} />
        ) : (
          <FilteringTable
            data={inventoryData}
            columns={columnsInventory}
            isInventory={true}
            setHiddenColumns={columns => setHiddenColumnsInventory(columns)}
            hiddenColumns={getArrayHiddenColumns(hiddenColumnsInventory)}
            enableSorting={true}
            handleSortChange={handleSortChange}
          />
        )}
      </div>
    </Modal>
  )
}

export default InventoryModal
